import * as Sentry from "@sentry/browser";

import BarrelUser from "../api/model/barrels/BarrelUser";
import Font from "../model/font";
import StyleguideData from "../model/StyleguideData";
import { PLATFORM } from "../enums";

import BasicRecord from "./BasicRecord";
import { uppercaseFirst } from "./string";

function isThereAnyMentionedUser(users: BarrelUser[], note: string, useUserIdForMention = false): boolean {
    const mentionKeywords = users
        .filter(({ user: { username } }) => username && username !== Zeplin.user.username)
        .map(({ user: { username, _id } }) => (useUserIdForMention ? _id : username)).join("|");
    const mentionRegex = new RegExp(`@(?:${mentionKeywords})\\b`, "gi");

    return mentionRegex.test(note);
}

/**
 * Validates given id according to ObjectId rules
 */
function validateId(id: string | null | undefined, type = "object"): boolean {
    if (!id || !/^[0-9a-f]{24}$/i.test(id)) {
        Sentry.withScope(scope => {
            scope.setExtra("id", id);
            Sentry.captureException(new Error(`Invalid ${type} id`));
        });

        return false;
    }

    return true;
}

const ORG_ROLES = ["king", "queen", "rook", "knight", "pawn"];

function isOrganizationProject(project: { users: BarrelUser[]; }): boolean {
    return !project.users.length || ORG_ROLES.includes(project.users[0].role);
}

/**
 * Normalizes color component for obj c
 */
function normalizeObjCColor(colorComponent: number): string {
    const roundedColorComponent = Math.round(colorComponent);

    if (!roundedColorComponent) {
        return "0.0f ";
    }

    if (roundedColorComponent === 255) {
        return "1.0f ";
    }

    return `${roundedColorComponent}.0f / 255.0f `;
}

/**
 * Normalizes color component for swift
 */
function normalizeSwiftColor(colorComponent: number): string {
    const roundedColorComponent = Math.round(colorComponent);

    if (!roundedColorComponent) {
        return "0.0, ";
    }

    if (roundedColorComponent === 255) {
        return "1.0, ";
    }

    return `${roundedColorComponent}.0 / 255.0, `;
}

const DEVELOPER_TYPES: BasicRecord = {
    web: "Web developer",
    android: "Android developer",
    ios: "iOS developer",
    mac: "macOS developer",
    osx: "macOS developer"
};

function getDeveloperString(platform: string): string {
    return DEVELOPER_TYPES[platform];
}

const alphaFormatter = new Intl.NumberFormat("en-US", {
    useGrouping: false,
    minimumFractionDigits: 1,
    maximumFractionDigits: 2
});

const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD"
});

function formatCurrency(price: number): string {
    return currencyFormatter.format(price);
}

function formatList(list: string[], type = "conjunction"): string {
    if (Intl.ListFormat) {
        return new Intl.ListFormat("en-US", { type }).format(list.map(String));
    }

    const separator = type === "disjunction" ? "or" : "and";

    if (list.length < 3) {
        return list.join(` ${separator} `);
    }

    return `${list.slice(0, -1).join(", ")}, ${separator} ${list[list.length - 1]}`;
}

const USER_ROLE_STRINGS: BasicRecord = {
    // new roles
    king: "Owner",
    queen: "Admin",
    rook: "Editor",
    knight: "Developer",
    pawn: "Reviewer",
    // old roles
    owner: "Owner",
    admin: "Admin",
    user: "Member"
};

function getUserRoleString(role: string): string {
    return USER_ROLE_STRINGS[role] ?? "";
}

function getRoleIconName(role: string): string {
    return `ic${uppercaseFirst(role)}`;
}

const densityScales: BasicRecord<number> = Object.freeze({
    "mdpi": 1,
    "1x": 1,
    "web": 1,
    "unknown": 1,
    "undefined": 1,

    "hdpi": 1.5,

    "xhdpi": 2,
    "2x": 2,

    "xxhdpi": 3,
    "3x": 3,

    "xxxhdpi": 4,
    "4x": 4
});

function getDensityScale(density: string): number {
    return densityScales[density];
}

function getMostConformingTextStyleInStyleguide(
    textStyles: Font[],
    textStyle: Font,
    densityScale: number,
    styleguideDensityScale: number
): {
    textStyle: Font | null;
    score: number;
} {
    return textStyles.reduce<{
        textStyle: Font | null;
        score: number;
    }>((accumulator, ts: Font) => {
        if (textStyle.sourceId && ts.sourceId && textStyle.sourceId === ts.sourceId) {
            // TODO: Instead of giving a higher score,
            // use forof and break if sourceIds are the same.
            // Do the same thing in getMostConformingTextStyle as well.
            return { textStyle: ts, score: 1000 };
        }

        if (!textStyle.conforms(ts, densityScale, styleguideDensityScale)) {
            return accumulator;
        }

        const textStylePropCount = Object.keys(ts)
            .filter(key => typeof (ts as unknown as BasicRecord<unknown>)[key] !== "undefined").length;
        if (textStylePropCount > accumulator.score) {
            return { textStyle: ts, score: textStylePropCount };
        }

        return accumulator;
    }, { textStyle: null, score: 0 });
}

function getMostConformingTextStyle(
    styleguidesData: StyleguideData[],
    textStyleToMatch: Font,
    densityScale: number
) {
    const conformingTextStyleResult = styleguidesData.reduce<{
        textStyle: Font | null;
        score: number;
        styleguideId?: string;
        styleguideName?: string;
        isMember?: boolean;
        isOrganizationBarrel?: boolean;
    }>((accumulator, styleguide) => {
        const {
            id,
            name,
            textStyles,
            isMember,
            isOrganizationBarrel,
            densityScale: styleguideDensityScale
        } = styleguide;

        const conformingTextStyle = getMostConformingTextStyleInStyleguide(
            textStyles,
            textStyleToMatch,
            densityScale,
            styleguideDensityScale!
        );

        if (conformingTextStyle.score > accumulator.score) {
            return {
                styleguideId: id,
                styleguideName: name,
                isMember,
                isOrganizationBarrel,
                ...conformingTextStyle
            };
        }

        return accumulator;
    }, { textStyle: null, score: 0 });

    if (!conformingTextStyleResult.textStyle) {
        return null;
    }

    const {
        styleguideId,
        styleguideName,
        textStyle,
        isMember,
        isOrganizationBarrel
    } = conformingTextStyleResult;

    return {
        styleguideId,
        styleguideName,
        isMember,
        isOrganizationBarrel,
        ...textStyle
    };
}

function stopPropagation(ev: Event) {
    ev.stopPropagation();
}

function preventDefault(ev: Event) {
    ev.preventDefault();
}

const iconWidth = 16;
const badgeSize = 13;
const middlePointSize = 2;
const overlayIconCanvas = document.createElement("canvas");

function half(d: number): number {
    return (d / 2) * window.devicePixelRatio;
}

function calculateOverlayIcon(): string {
    overlayIconCanvas.width = iconWidth * window.devicePixelRatio;
    overlayIconCanvas.height = overlayIconCanvas.width;
    const ctx = overlayIconCanvas.getContext("2d")!;

    ctx.beginPath();
    ctx.fillStyle = "#ee6723";
    ctx.arc(half(iconWidth), half(iconWidth), half(badgeSize), 0, 6.28, false);
    ctx.fill();
    ctx.beginPath();
    ctx.fillStyle = "#fff";
    ctx.arc(half(iconWidth), half(iconWidth), half(middlePointSize), 0, 6.28, false);
    ctx.fill();
    return overlayIconCanvas.toDataURL();
}

// TODO: Type the method correctly
/**
 * Pipes given value to provided functions
 * @params {function} arguments of functions
 * @returns final result after all functions applied in given order
 */
function pipe(...funcs: any[]): any { // eslint-disable-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return funcs.reduce((acc, curr) => (x: any) => curr(acc(x)), (x: any) => x);
}

/**
 * Returns the minimal state update function that updates current state with rawStateUpdate
 * If current state is already has same values with rawStateUpdate, it returns update function that returns null
 *
 * @returns state update function that returns currentState updated with rawStateUpdate or null
 */
function getStateUpdateFunction(rawStateUpdate: BasicRecord<unknown>):
    (prevState: BasicRecord<unknown>) => BasicRecord<unknown> | null {
    return (prevState: BasicRecord<unknown>) =>
        Object.keys(rawStateUpdate).reduce<BasicRecord<unknown> | null>((acc, stateKey) => {
            if (prevState[stateKey] === rawStateUpdate[stateKey]) {
                return acc;
            }

            acc ??= {}; // eslint-disable-line no-param-reassign
            acc[stateKey] = rawStateUpdate[stateKey];
            return acc;
        }, null);
}

/**
 * Creates the clone of given object excluding given property
 * @param obj object to be cloned
 * @param key property key to be removed
 */
function filterOut<T>(obj: BasicRecord<T>, key: string): BasicRecord<T> {
    const { [key]: _, ...newObj } = obj;
    return newObj;
}

function fontSort(prev: Font, cur: Font): number {
    // By font size
    if (prev.fontSize > cur.fontSize) {
        return -1;
    }

    if (prev.fontSize < cur.fontSize) {
        return 1;
    }

    // By font weight
    if (prev.fontWeight > cur.fontWeight) {
        return -1;
    }

    if (prev.fontWeight < cur.fontWeight) {
        return 1;
    }

    // By color
    if (prev.color && !cur.color) {
        return -1;
    }

    if (cur.color && !prev.color) {
        return 1;
    }

    // By font face
    if (prev.fontFace < cur.fontFace) {
        return -1;
    }

    if (prev.fontFace > cur.fontFace) {
        return 1;
    }

    // By name
    if (prev.name < cur.name) {
        return -1;
    }

    return 1;
}

const androidDensityScaleRepresentations: Record<number, string> = Object.freeze({
    1: "mdpi",
    1.5: "hdpi",
    2: "xhdpi",
    3: "xxhdpi",
    4: "xxxhdpi"
});

function getDensityScaleRepresentation(platform: PLATFORM, densityScale?: number): string {
    if (!densityScale) {
        return "unknown";
    }

    if (platform === PLATFORM.ANDROID) {
        return androidDensityScaleRepresentations[densityScale];
    }

    return `${densityScale}x`;
}

function areRectsIntersecting(targetRect: DOMRectReadOnly, rootRect: DOMRectReadOnly): boolean {
    if (!targetRect || !rootRect) {
        return false;
    }

    return !(
        rootRect.left >= targetRect.right ||
        rootRect.top >= targetRect.bottom ||
        rootRect.right <= targetRect.left ||
        rootRect.bottom <= targetRect.top
    );
}

export {
    fontSort,
    isThereAnyMentionedUser,
    validateId,
    isOrganizationProject,
    getDeveloperString,
    normalizeObjCColor,
    normalizeSwiftColor,
    alphaFormatter,
    getUserRoleString,
    getRoleIconName,
    getDensityScale,
    getDensityScaleRepresentation,
    getMostConformingTextStyle,
    stopPropagation,
    preventDefault,
    formatCurrency,
    formatList,
    calculateOverlayIcon,
    pipe,
    getStateUpdateFunction,
    filterOut,
    areRectsIntersecting
};
